/**
 * Form Utilities
 * Reusable functions for form data transformations
 */

/**
 * Split full name into first name and last name
 */
export const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  if (!fullName || typeof fullName !== "string") {
    return { firstName: "", lastName: "" };
  }

  const nameParts = fullName.trim().split(" ");
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  return { firstName, lastName };
};

/**
 * Combine first name and last name into full name
 */
export const combineFullName = (firstName: string, lastName: string): string => {
  const first = firstName?.trim() || "";
  const last = lastName?.trim() || "";

  if (!first && !last) return "";
  if (!first) return last;
  if (!last) return first;

  return `${first} ${last}`;
};

/**
 * Extract postal/zip code from a string using international patterns
 * Supports postal codes from US, UK, Canada, and many other countries
 */
export const extractPostalCode = (text: string): string => {
  if (!text || typeof text !== "string") return "";

  // Postal code patterns for various countries
  const postalCodePatterns = [
    // UK postcodes (e.g., NE4 9NE, SW1A 1AA, EC1A 1BB)
    /\b([A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2})\b/gi,

    // US ZIP codes (e.g., 12345, 12345-6789)
    /\b(\d{5}(?:-\d{4})?)\b/g,

    // Canadian postal codes (e.g., K1A 0B1, M5V 3A8)
    /\b([A-Z]\d[A-Z]\s?\d[A-Z]\d)\b/gi,

    // Australian postcodes (4 digits, e.g., 2000, 3000)
    /\b(\d{4})\b/g,

    // German postcodes (5 digits, e.g., 10115)
    /\b(\d{5})\b/g,

    // French postcodes (5 digits, e.g., 75001)
    /\b(\d{5})\b/g,

    // Netherlands postcodes (e.g., 1012 AB)
    /\b(\d{4}\s?[A-Z]{2})\b/gi,

    // Spanish postcodes (5 digits, e.g., 28001)
    /\b(\d{5})\b/g,

    // Italian postcodes (5 digits, e.g., 00100)
    /\b(\d{5})\b/g,

    // Irish Eircode (e.g., D02 AF30)
    /\b([A-Z]\d{2}\s?[A-Z0-9]{4})\b/gi,

    // Portuguese postcodes (e.g., 1000-001)
    /\b(\d{4}-\d{3})\b/g,

    // Swiss postcodes (4 digits, e.g., 8001)
    /\b(\d{4})\b/g,

    // Indian PIN codes (6 digits, e.g., 110001)
    /\b(\d{6})\b/g,

    // Japanese postal codes (e.g., 100-0001)
    /\b(\d{3}-\d{4})\b/g,

    // Brazilian CEP (e.g., 01310-100)
    /\b(\d{5}-\d{3})\b/g,

    // Mexican postal codes (5 digits, e.g., 01000)
    /\b(\d{5})\b/g,

    // Polish postcodes (e.g., 00-001)
    /\b(\d{2}-\d{3})\b/g,

    // Swedish postcodes (e.g., 123 45)
    /\b(\d{3}\s?\d{2})\b/g,

    // Norwegian postcodes (4 digits, e.g., 0001)
    /\b(\d{4})\b/g,

    // Danish postcodes (4 digits, e.g., 1000)
    /\b(\d{4})\b/g,

    // Finnish postcodes (5 digits, e.g., 00100)
    /\b(\d{5})\b/g,

    // Belgian postcodes (4 digits, e.g., 1000)
    /\b(\d{4})\b/g,

    // Czech postcodes (e.g., 100 00)
    /\b(\d{3}\s?\d{2})\b/g,

    // Slovak postcodes (e.g., 811 01)
    /\b(\d{3}\s?\d{2})\b/g,

    // Hungarian postcodes (4 digits, e.g., 1011)
    /\b(\d{4})\b/g,

    // Romanian postcodes (6 digits, e.g., 010001)
    /\b(\d{6})\b/g,

    // South African postcodes (4 digits, e.g., 0001)
    /\b(\d{4})\b/g,

    // Argentine postal codes (e.g., C1234ABC)
    /\b([A-Z]\d{4}[A-Z]{3})\b/gi,

    // Chilean postal codes (7 digits, e.g., 8320000)
    /\b(\d{7})\b/g,
  ];

  // Try each pattern and return the first match
  for (const pattern of postalCodePatterns) {
    const match = text.match(pattern);
    if (match && match[0]) {
      // Return the matched postal code, trimmed
      return match[0].trim();
    }
  }

  return "";
};

/**
 * Parse address components from a combined address string
 * Enhanced with postal code extraction
 * Supports both comma-separated format: "City, State, Zip"
 * And space-separated format: "City State Zip" (e.g., "Sitwell WA 98001")
 */
export const parseAddressComponents = (
  addressString: string
): {
  city: string;
  state: string;
  postalCode: string;
} => {
  const result = { city: "", state: "", postalCode: "" };

  if (!addressString || typeof addressString !== "string") {
    return result;
  }

  console.log("🔍 parseAddressComponents - Input:", addressString);

  // First, extract postal code from the entire string
  const extractedPostalCode = extractPostalCode(addressString);
  if (extractedPostalCode) {
    result.postalCode = extractedPostalCode;
    console.log("✅ Extracted postal code:", extractedPostalCode);
    // Remove postal code from string
    addressString = addressString.replace(extractedPostalCode, "").trim();
    console.log("🔍 After removing postal code:", addressString);
  }

  const hasCommas = addressString.includes(",");

  if (hasCommas) {
    const parts = addressString
      .split(",")
      .map((part) => part.trim())
      .filter((part) => part);

    console.log("🔍 Comma-separated parts:", parts);

    // If we have at least one part, it's the city
    if (parts.length > 0) {
      result.city = parts[0];
    }

    // If we have a second part, check if it contains a postal code
    if (parts.length > 1) {
      const secondPart = parts[1];
      const postalCodeInSecondPart = extractPostalCode(secondPart);

      if (postalCodeInSecondPart) {
        // Second part contains both state and postal code
        const stateOnly = secondPart.replace(postalCodeInSecondPart, "").trim();
        result.state = stateOnly;
        if (!result.postalCode) {
          result.postalCode = postalCodeInSecondPart;
        }
        console.log(
          "🔍 Second part had postal code - State:",
          stateOnly,
          "Postal:",
          postalCodeInSecondPart
        );
      } else {
        result.state = secondPart;
      }
    }

    // If we have a third part and no postal code yet, it's the postal code
    if (parts.length > 2 && !result.postalCode) {
      result.postalCode = parts[2];
    }
  } else {
    const parts = addressString.split(/\s+/).filter((part) => part);

    if (parts.length === 0) {
      // Nothing left after removing postal code
      // Postal code is already set above
    } else if (parts.length === 1) {
      // Only city left
      result.city = parts[0];
    } else if (parts.length === 2) {
      // Check if second part is a 2-letter state code
      if (/^[A-Z]{2}$/i.test(parts[1])) {
        result.city = parts[0];
        result.state = parts[1].toUpperCase();
      } else {
        // Assume multi-word city name
        result.city = parts.join(" ");
      }
    } else {
      const stateIndex = parts.findIndex((part) => /^[A-Z]{2}$/i.test(part));

      if (stateIndex > 0) {
        result.city = parts.slice(0, stateIndex).join(" ");
        result.state = parts[stateIndex].toUpperCase();
      } else {
        result.state = parts[parts.length - 1];
        result.city = parts.slice(0, -1).join(" ");
      }
    }
  }

  // If state contains a postal code that wasn't extracted, try to separate it
  if (result.state && !result.postalCode) {
    const statePostalCode = extractPostalCode(result.state);
    if (statePostalCode) {
      result.postalCode = statePostalCode;
      result.state = result.state.replace(statePostalCode, "").trim();
    }
  }
  return result;
};

/**
 * Combine address components into a single string
 */
export const combineAddressComponents = (
  city: string,
  state: string,
  postalCode: string
): string => {
  const cityPart = city?.trim() || "";
  const statePart = state?.trim() || "";
  const postalPart = postalCode?.trim() || "";

  const parts = [cityPart, statePart, postalPart].filter((part) => part !== "");

  return parts.join(", ");
};

/**
 * Split mailing address into separate line components
 */
export const splitMailingAddress = (
  address: string
): {
  line1: string;
  line2: string;
  line3: string;
} => {
  if (!address || typeof address !== "string") {
    return { line1: "", line2: "", line3: "" };
  }

  // Split by line breaks or semicolons
  const lines = address
    .split(/[\n;]/)
    .map((line) => line.trim())
    .filter((line) => line !== "");

  return {
    line1: lines[0] || "",
    line2: lines[1] || "",
    line3: lines[2] || "",
  };
};

/**
 * Combine address lines into a single address string
 */
export const combineAddressLines = (line1: string, line2: string, line3: string): string => {
  const lines = [line1?.trim(), line2?.trim(), line3?.trim()].filter((line) => line && line !== "");
  return lines.join("\n");
};

/**
 * Format TIN based on type
 */
export const formatTIN = (value: string, type: "EIN" | "SSN" | "ITIN"): string => {
  if (!value) return "";

  // Remove all non-digits
  const digits = value.replace(/\D/g, "");

  switch (type) {
    case "EIN":
      // Format as XX-XXXXXXX
      if (digits.length >= 3) {
        return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
      }
      return digits.slice(0, 2);

    case "SSN":
    case "ITIN":
      // Format as XXX-XX-XXXX
      if (digits.length >= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
      } else if (digits.length >= 4) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}`;
      }
      return digits.slice(0, 3);

    default:
      return digits;
  }
};

/**
 * Parse date to consistent format
 */
export const parseDate = (dateString: string): string => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid

    // Return in MM/DD/YYYY format
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    return dateString; // Return original if parsing fails
  }
};

/**
 * Validate required fields
 */
export const validateRequiredFields = (
  data: Record<string, any>,
  requiredFields: string[]
): {
  isValid: boolean;
  errors: string[];
  fieldErrors: Record<string, string>;
} => {
  const errors: string[] = [];
  const fieldErrors: Record<string, string> = {};

  requiredFields.forEach((field) => {
    const value = data[field];
    if (!value || (typeof value === "string" && value.trim() === "")) {
      const fieldName = field
        .replace(/[-_]/g, " ")
        .replace(/([A-Z])/g, " $1")
        .trim();
      const error = `${fieldName} is required`;
      errors.push(error);
      fieldErrors[field] = error;
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    fieldErrors,
  };
};

/**
 * Detect TIN type based on the TIN value format
 * Includes support for special EIN types (QIEIN, WPEIN, WTEIN) and GIIN
 * @param tin - The taxpayer identification number
 * @returns TIN type: 'EIN', 'SSN', 'ITIN', 'QIEIN', 'WPEIN', 'WTEIN', 'GIIN', or null if cannot be determined
 */
export const detectTINType = (
  tin: string
): "EIN" | "SSN" | "ITIN" | "SSN/ITIN" | "QIEIN" | "WPEIN" | "WTEIN" | "GIIN" | null => {
  if (!tin || typeof tin !== "string") return null;

  const trimmedTIN = tin.trim();

  // Check for special EIN suffixes (used in W-8IMY and W-8BEN-E) - MUST have dashes
  if (/^\d{2}-\d{7}-QI$/i.test(trimmedTIN)) return "QIEIN"; // Qualified Intermediary EIN
  if (/^\d{2}-\d{7}-WP$/i.test(trimmedTIN)) return "WPEIN"; // Withholding Partnership EIN
  if (/^\d{2}-\d{7}-WT$/i.test(trimmedTIN)) return "WTEIN"; // Withholding Trust EIN

  // Check for GIIN format (Global Intermediary Identification Number)
  // Format: XXXXXX.XXXXX.XX.XXX (6 digits, 5 digits, 2 digits, 3 digits separated by dots)
  if (/^[A-Z0-9]{6}\.[A-Z0-9]{5}\.[A-Z0-9]{2}\.[A-Z0-9]{3}$/i.test(trimmedTIN)) return "GIIN";

  // Check for properly formatted TINs with dashes FIRST
  // EIN format: XX-XXXXXXX (must have dash in correct position)
  if (/^\d{2}-\d{7}$/.test(trimmedTIN)) {
    const firstTwoDigits = parseInt(trimmedTIN.substring(0, 2));
    if (firstTwoDigits >= 10 && firstTwoDigits <= 99) {
      return "EIN";
    }
  }

  // SSN format: XXX-XX-XXXX (must have dashes in correct positions)
  if (/^\d{3}-\d{2}-\d{4}$/.test(trimmedTIN)) {
    const firstDigit = trimmedTIN[0];
    // Check if it's an ITIN (starts with 9 and has specific middle digits)
    if (firstDigit === "9") {
      const fourthFifthDigits = parseInt(trimmedTIN.substring(4, 6));
      if (
        (fourthFifthDigits >= 50 && fourthFifthDigits <= 65) ||
        (fourthFifthDigits >= 70 && fourthFifthDigits <= 88) ||
        (fourthFifthDigits >= 90 && fourthFifthDigits <= 92) ||
        (fourthFifthDigits >= 94 && fourthFifthDigits <= 99)
      ) {
        return "ITIN";
      }
    }
    return "SSN";
  }

  // If no dashes found, check if it's 9 digits without formatting
  // This is considered INVALID format, so return null
  const cleanTIN = trimmedTIN.replace(/\D/g, "");
  if (cleanTIN.length === 9) {
    // Has 9 digits but no proper formatting - return null to show warning
    return null;
  }

  // Not a valid TIN format
  return null;
};

/**
 * Validate TIN format based on selected type
 * Supports standard TINs and special types (QIEIN, WPEIN, WTEIN, GIIN)
 * @param tin - The taxpayer identification number
 * @param type - The selected TIN type
 * @returns Validation result with error message if invalid
 */
export const validateTINFormat = (
  tin: string,
  type: string
): {
  isValid: boolean;
  message: string | null;
  detectedType: string | null;
} => {
  if (!tin || tin.trim() === "") {
    return { isValid: true, message: null, detectedType: null };
  }

  const trimmedTIN = tin.trim();
  const detectedType = detectTINType(trimmedTIN);

  // Special handling for GIIN - it's a separate field, not a TIN type
  if (detectedType === "GIIN") {
    return {
      isValid: false,
      message: `⚠️ This appears to be a GIIN (format: XXXXXX.XXXXX.XX.XXX). Please enter it in the separate "GIIN" field below, not in the TIN field.`,
      detectedType: "GIIN",
    };
  }

  // Special handling for QIEIN, WPEIN, WTEIN (have suffixes)
  if (detectedType === "QIEIN" || detectedType === "WPEIN" || detectedType === "WTEIN") {
    if (!type || type === "" || type === "---Select---") {
      return {
        isValid: true,
        message: `✓ Detected as ${detectedType}`,
        detectedType: detectedType,
      };
    }
    if (type.toUpperCase() === detectedType) {
      return {
        isValid: true,
        message: `✓ Valid ${detectedType} format`,
        detectedType: detectedType,
      };
    }
    return {
      isValid: false,
      message: `⚠️ TIN format doesn't match selected type ${type}. Detected as ${detectedType}`,
      detectedType: detectedType,
    };
  }

  const cleanTIN = trimmedTIN.replace(/\D/g, "");

  // Check if it's 9 digits but missing proper formatting
  if (cleanTIN.length === 9 && !detectedType) {
    // Has 9 digits but not properly formatted with dashes
    const firstTwoDigits = parseInt(cleanTIN.substring(0, 2));
    const firstDigit = cleanTIN[0];

    // If type is selected, show the expected format for that type
    if (type && type !== "" && type !== "---Select---") {
      const normalizedType = type.toUpperCase();

      if (normalizedType === "EIN") {
        const suggestedFormat = `${cleanTIN.substring(0, 2)}-${cleanTIN.substring(2)}`;
        return {
          isValid: false,
          message: `⚠️ Invalid format. Expected EIN format: XX-XXXXXXX (e.g., ${suggestedFormat})`,
          detectedType: null,
        };
      } else if (normalizedType === "QIEIN") {
        const suggestedFormat = `${cleanTIN.substring(0, 2)}-${cleanTIN.substring(2)}-QI`;
        return {
          isValid: false,
          message: `⚠️ Invalid format. Expected QIEIN format: XX-XXXXXXX-QI (e.g., ${suggestedFormat})`,
          detectedType: null,
        };
      } else if (normalizedType === "WPEIN") {
        const suggestedFormat = `${cleanTIN.substring(0, 2)}-${cleanTIN.substring(2)}-WP`;
        return {
          isValid: false,
          message: `⚠️ Invalid format. Expected WPEIN format: XX-XXXXXXX-WP (e.g., ${suggestedFormat})`,
          detectedType: null,
        };
      } else if (normalizedType === "WTEIN") {
        const suggestedFormat = `${cleanTIN.substring(0, 2)}-${cleanTIN.substring(2)}-WT`;
        return {
          isValid: false,
          message: `⚠️ Invalid format. Expected WTEIN format: XX-XXXXXXX-WT (e.g., ${suggestedFormat})`,
          detectedType: null,
        };
      } else if (
        normalizedType === "SSN" ||
        normalizedType === "ITIN" ||
        normalizedType === "SSN/ITIN"
      ) {
        const suggestedFormat = `${cleanTIN.substring(0, 3)}-${cleanTIN.substring(3, 5)}-${cleanTIN.substring(5)}`;
        return {
          isValid: false,
          message: `⚠️ Invalid format. Expected ${type} format: XXX-XX-XXXX (e.g., ${suggestedFormat})`,
          detectedType: null,
        };
      }
    }

    // If no type selected, try to suggest the correct format based on digits
    if (firstTwoDigits >= 10 && firstTwoDigits <= 99 && firstDigit !== "9") {
      // Looks like an EIN without dashes
      const suggestedFormat = `${cleanTIN.substring(0, 2)}-${cleanTIN.substring(2)}`;
      return {
        isValid: false,
        message: `⚠️ Invalid format. TIN has 9 digits but missing dashes. Expected EIN format: XX-XXXXXXX (e.g., ${suggestedFormat})`,
        detectedType: null,
      };
    } else if (firstDigit === "9" || firstDigit !== "0") {
      // Looks like SSN/ITIN without dashes
      const suggestedFormat = `${cleanTIN.substring(0, 3)}-${cleanTIN.substring(3, 5)}-${cleanTIN.substring(5)}`;
      return {
        isValid: false,
        message: `⚠️ Invalid format. TIN has 9 digits but missing dashes. Expected SSN/ITIN format: XXX-XX-XXXX (e.g., ${suggestedFormat})`,
        detectedType: null,
      };
    }
  }

  // Check if it's not 9 digits
  if (cleanTIN.length !== 9) {
    // Check if type is selected and needs special format
    if (type && type !== "" && type !== "---Select---") {
      const normalizedType = type.toUpperCase();

      // GIIN should never be selected as a TIN type (it has its own separate field)
      if (normalizedType === "GIIN") {
        return {
          isValid: false,
          message: "⚠️ GIIN is not a valid TIN type. GIIN has its own separate field below.",
          detectedType: null,
        };
      } else if (
        normalizedType === "QIEIN" ||
        normalizedType === "WPEIN" ||
        normalizedType === "WTEIN"
      ) {
        const suffix = normalizedType === "QIEIN" ? "QI" : normalizedType === "WPEIN" ? "WP" : "WT";
        return {
          isValid: false,
          message: `⚠️ Invalid ${type} format. Expected format: XX-XXXXXXX-${suffix}`,
          detectedType: null,
        };
      } else if (normalizedType === "SSN/ITIN") {
        return {
          isValid: false,
          message: `⚠️ TIN must be 9 digits. Expected format: SSN/ITIN (XXX-XX-XXXX)`,
          detectedType: null,
        };
      } else if (normalizedType === "EIN") {
        return {
          isValid: false,
          message: `⚠️ TIN must be 9 digits. Expected format: EIN (XX-XXXXXXX)`,
          detectedType: null,
        };
      } else {
        return {
          isValid: false,
          message: `⚠️ TIN must be 9 digits. Expected ${type} format.`,
          detectedType: null,
        };
      }
    }

    return {
      isValid: false,
      message: "⚠️ TIN must be 9 digits. No valid TIN type detected for this value.",
      detectedType: null,
    };
  }

  // If no type is selected, just detect and return
  if (!type || type === "" || type === "---Select---") {
    if (detectedType) {
      return {
        isValid: true,
        message: `✓ Detected as ${detectedType}`,
        detectedType: detectedType,
      };
    } else {
      return {
        isValid: false,
        message: "⚠️ No TIN type matched this value. Please select the correct type manually.",
        detectedType: null,
      };
    }
  }

  // If type is selected, validate against that type
  const normalizedType = type.toUpperCase();
  const detectedNormalized = detectedType?.toUpperCase();

  // Handle combined SSN/ITIN option
  if (normalizedType === "SSN/ITIN") {
    if (detectedNormalized === "SSN" || detectedNormalized === "ITIN") {
      return {
        isValid: true,
        message: `✓ Valid ${detectedType} format`,
        detectedType: detectedType,
      };
    } else if (detectedType) {
      return {
        isValid: false,
        message: `⚠️ TIN format doesn't match ${type}. Detected as ${detectedType}`,
        detectedType: detectedType,
      };
    } else {
      return {
        isValid: false,
        message: `⚠️ No TIN type matched this value.`,
        detectedType: null,
      };
    }
  }

  // Validate specific types
  if (detectedNormalized === normalizedType) {
    return {
      isValid: true,
      message: `✓ Valid ${type} format`,
      detectedType: detectedType,
    };
  } else if (detectedType) {
    return {
      isValid: false,
      message: `⚠️ TIN format doesn't match selected type ${type}. Detected as ${detectedType}`,
      detectedType: detectedType,
    };
  } else {
    return {
      isValid: false,
      message: `⚠️ No TIN type matched this value for selected type ${type}.`,
      detectedType: null,
    };
  }
};

/**
 * Get maximum length for TIN input based on type
 * @param type - The TIN type
 * @returns Maximum length including dashes and suffixes
 */
export const getMaxTINLength = (type: string): number => {
  if (!type || type === "" || type === "---Select---") {
    return 20; // Allow more when no type selected for auto-detection
  }

  const normalizedType = type.toUpperCase();

  switch (normalizedType) {
    case "EIN":
      return 10; // XX-XXXXXXX (2-7 with dash)
    case "SSN":
    case "ITIN":
    case "SSN/ITIN":
      return 11; // XXX-XX-XXXX (3-2-4 with dashes)
    case "QIEIN":
      return 13; // XX-XXXXXXX-QI (2-7-QI with dashes)
    case "WPEIN":
      return 13; // XX-XXXXXXX-WP (2-7-WP with dashes)
    case "WTEIN":
      return 13; // XX-XXXXXXX-WT (2-7-WT with dashes)
    // Note: GIIN is not a TIN type - it has its own separate field
    default:
      return 20; // Default max for unknown types
  }
};

/**
 * Validate and check if a value matches a dropdown list
 * @param value - The value to check
 * @param validOptions - Array of valid options
 * @returns Object with validation status and message
 */
export const validateDropdownValue = (
  value: string,
  validOptions: string[],
  fieldName: string = "Value"
): {
  isValid: boolean;
  message: string | null;
} => {
  if (!value || value.trim() === "") {
    return { isValid: true, message: null };
  }

  const isValid = validOptions.includes(value);

  return {
    isValid,
    message: isValid ? null : `⚠️ ${fieldName} from API: "${value}" - Not found in dropdown list`,
  };
};

/**
 * Check if a state code or name is a US state or territory
 * Supports both 2-letter codes (CA, NY) and full names (California, New York)
 */
export const isUSStateOrTerritory = (stateInput: string): boolean => {
  if (!stateInput || typeof stateInput !== "string") {
    return false;
  }

  // Convert to uppercase for comparison
  const state = stateInput.trim().toUpperCase();

  // US States - codes and full names
  const usStatesMap = new Map([
    ["AL", "ALABAMA"],
    ["AK", "ALASKA"],
    ["AZ", "ARIZONA"],
    ["AR", "ARKANSAS"],
    ["CA", "CALIFORNIA"],
    ["CO", "COLORADO"],
    ["CT", "CONNECTICUT"],
    ["DE", "DELAWARE"],
    ["FL", "FLORIDA"],
    ["GA", "GEORGIA"],
    ["HI", "HAWAII"],
    ["ID", "IDAHO"],
    ["IL", "ILLINOIS"],
    ["IN", "INDIANA"],
    ["IA", "IOWA"],
    ["KS", "KANSAS"],
    ["KY", "KENTUCKY"],
    ["LA", "LOUISIANA"],
    ["ME", "MAINE"],
    ["MD", "MARYLAND"],
    ["MA", "MASSACHUSETTS"],
    ["MI", "MICHIGAN"],
    ["MN", "MINNESOTA"],
    ["MS", "MISSISSIPPI"],
    ["MO", "MISSOURI"],
    ["MT", "MONTANA"],
    ["NE", "NEBRASKA"],
    ["NV", "NEVADA"],
    ["NH", "NEW HAMPSHIRE"],
    ["NJ", "NEW JERSEY"],
    ["NM", "NEW MEXICO"],
    ["NY", "NEW YORK"],
    ["NC", "NORTH CAROLINA"],
    ["ND", "NORTH DAKOTA"],
    ["OH", "OHIO"],
    ["OK", "OKLAHOMA"],
    ["OR", "OREGON"],
    ["PA", "PENNSYLVANIA"],
    ["RI", "RHODE ISLAND"],
    ["SC", "SOUTH CAROLINA"],
    ["SD", "SOUTH DAKOTA"],
    ["TN", "TENNESSEE"],
    ["TX", "TEXAS"],
    ["UT", "UTAH"],
    ["VT", "VERMONT"],
    ["VA", "VIRGINIA"],
    ["WA", "WASHINGTON"],
    ["WV", "WEST VIRGINIA"],
    ["WI", "WISCONSIN"],
    ["WY", "WYOMING"],
  ]);

  // US Territories - codes and full names
  const usTerritoriesMap = new Map([
    ["AS", "AMERICAN SAMOA"],
    ["GU", "GUAM"],
    ["MP", "NORTHERN MARIANA ISLANDS"],
    ["PR", "PUERTO RICO"],
    ["VI", "U.S. VIRGIN ISLANDS"],
    ["VI", "US VIRGIN ISLANDS"],
    ["VI", "VIRGIN ISLANDS"],
  ]);

  // Check if input matches any state code
  if (usStatesMap.has(state) || usTerritoriesMap.has(state)) {
    return true;
  }

  // Check if input matches any state name
  const stateNames = Array.from(usStatesMap.values());
  const territoryNames = Array.from(usTerritoriesMap.values());
  const allStateNames = stateNames.concat(territoryNames);
  return allStateNames.includes(state);
};

/**
 * Check if a state is a Canadian province or territory
 */
export const isCanadianProvince = (stateInput: string): boolean => {
  if (!stateInput || typeof stateInput !== "string") {
    return false;
  }

  const state = stateInput.trim().toUpperCase();

  const canadianProvincesMap = new Map([
    ["AB", "ALBERTA"],
    ["BC", "BRITISH COLUMBIA"],
    ["MB", "MANITOBA"],
    ["NB", "NEW BRUNSWICK"],
    ["NL", "NEWFOUNDLAND AND LABRADOR"],
    ["NS", "NOVA SCOTIA"],
    ["NT", "NORTHWEST TERRITORIES"],
    ["NU", "NUNAVUT"],
    ["ON", "ONTARIO"],
    ["PE", "PRINCE EDWARD ISLAND"],
    ["QC", "QUEBEC"],
    ["SK", "SASKATCHEWAN"],
    ["YT", "YUKON"],
  ]);

  // Check if input matches any province code
  if (canadianProvincesMap.has(state)) {
    return true;
  }

  // Check if input matches any province name
  const provinceNames = Array.from(canadianProvincesMap.values());
  return provinceNames.includes(state);
};

/**
 * Determine which state field to use based on country and state value
 * Returns 'USCA' for US/Canada states or 'NonUS' for other states
 */
export const determineStateFieldType = (country: string, state: string): "USCA" | "NonUS" => {
  if (!state || typeof state !== "string") {
    return "NonUS";
  }

  // If country is provided, check if it's US or Canada
  if (country && typeof country === "string") {
    const countryLower = country.toLowerCase().trim();
    const isUSOrCanada =
      countryLower.includes("united states") ||
      countryLower.includes("canada") ||
      countryLower === "us" ||
      countryLower === "usa" ||
      countryLower === "ca" ||
      countryLower === "can";

    if (isUSOrCanada) {
      return "USCA";
    } else {
      return "NonUS";
    }
  }

  // If no country is specified, check if state matches US/Canada states
  if (isUSStateOrTerritory(state) || isCanadianProvince(state)) {
    return "USCA";
  }

  return "NonUS";
};

/**
 * Assign state value to appropriate field based on country
 * Returns an object with the correct field name and value
 */
export const assignStateToField = (
  country: string,
  state: string,
  fieldPrefix: string = "Permanent address"
): { fieldName: string; value: string } => {
  const fieldType = determineStateFieldType(country, state);
  const fieldName = `${fieldPrefix} state ${fieldType}`;

  return {
    fieldName,
    value: state,
  };
};

/**
 * Convert 2-letter US state/territory code to full state name
 * If input is not a 2-letter code or not found, returns the original value
 * @param stateCode - 2-letter state code (e.g., "CA", "NY")
 * @returns Full state name (e.g., "California", "New York") or original value if not found
 */
export const getFullStateName = (stateCode: string): string => {
  if (!stateCode || typeof stateCode !== "string") {
    return stateCode;
  }

  const trimmedCode = stateCode.trim();

  // Only convert if it's a 2-letter code
  if (trimmedCode.length !== 2) {
    return stateCode;
  }

  const code = trimmedCode.toUpperCase();

  // US States map
  const stateMap = new Map([
    ["AL", "Alabama"],
    ["AK", "Alaska"],
    ["AZ", "Arizona"],
    ["AR", "Arkansas"],
    ["CA", "California"],
    ["CO", "Colorado"],
    ["CT", "Connecticut"],
    ["DE", "Delaware"],
    ["FL", "Florida"],
    ["GA", "Georgia"],
    ["HI", "Hawaii"],
    ["ID", "Idaho"],
    ["IL", "Illinois"],
    ["IN", "Indiana"],
    ["IA", "Iowa"],
    ["KS", "Kansas"],
    ["KY", "Kentucky"],
    ["LA", "Louisiana"],
    ["ME", "Maine"],
    ["MD", "Maryland"],
    ["MA", "Massachusetts"],
    ["MI", "Michigan"],
    ["MN", "Minnesota"],
    ["MS", "Mississippi"],
    ["MO", "Missouri"],
    ["MT", "Montana"],
    ["NE", "Nebraska"],
    ["NV", "Nevada"],
    ["NH", "New Hampshire"],
    ["NJ", "New Jersey"],
    ["NM", "New Mexico"],
    ["NY", "New York"],
    ["NC", "North Carolina"],
    ["ND", "North Dakota"],
    ["OH", "Ohio"],
    ["OK", "Oklahoma"],
    ["OR", "Oregon"],
    ["PA", "Pennsylvania"],
    ["RI", "Rhode Island"],
    ["SC", "South Carolina"],
    ["SD", "South Dakota"],
    ["TN", "Tennessee"],
    ["TX", "Texas"],
    ["UT", "Utah"],
    ["VT", "Vermont"],
    ["VA", "Virginia"],
    ["WA", "Washington"],
    ["WV", "West Virginia"],
    ["WI", "Wisconsin"],
    ["WY", "Wyoming"],
    // US Territories
    ["AS", "American Samoa"],
    ["GU", "Guam"],
    ["MP", "Northern Mariana Islands"],
    ["PR", "Puerto Rico"],
    ["VI", "U.S. Virgin Islands"],
    // Canadian Provinces
    ["AB", "Alberta"],
    ["BC", "British Columbia"],
    ["MB", "Manitoba"],
    ["NB", "New Brunswick"],
    ["NL", "Newfoundland and Labrador"],
    ["NS", "Nova Scotia"],
    ["NT", "Northwest Territories"],
    ["NU", "Nunavut"],
    ["ON", "Ontario"],
    ["PE", "Prince Edward Island"],
    ["QC", "Quebec"],
    ["SK", "Saskatchewan"],
    ["YT", "Yukon"],
  ]);

  return stateMap.get(code) || stateCode;
};
